import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { datadogLogs } from '@datadog/browser-logs';
import { WindowRefService } from './window-ref.service';
import { environment } from '../../environments/environment';
import {
  SKIP_LOG_URL_PATTERNS,
  maskRoutePath,
  maskUrlForLogging,
  buildAllowedHosts,
} from '../utils/datadog-log-sanitizer.util';

export const DD_LOGGING_ENABLED = new InjectionToken<boolean>('DD_LOGGING_ENABLED', {
  providedIn: 'root',
  factory: () => !!environment.dataDogLogsConfig?.clientToken,
});

export const DD_LOG_CLIENT_ERRORS = new InjectionToken<boolean>('DD_LOG_CLIENT_ERRORS', {
  providedIn: 'root',
  // UAT includes 4xx responses temporarily to verify browser-log ingestion.
  // Production continues to report only backend and connectivity failures.
  factory: () => environment.env === 'uat',
});

export interface DdLogger {
  error: (message: string, context: object) => void;
}

export const DD_LOGGER = new InjectionToken<DdLogger>('DD_LOGGER', {
  providedIn: 'root',
  factory: () => datadogLogs.logger,
});

export type HttpFailureClass = 'backend' | 'client' | 'connectivity';

export interface HttpErrorLogContext {
  request: HttpRequest<unknown>;
  error: unknown;
}

export interface HttpErrorLogPayload {
  status: number;
  method: string;
  url_template: string;
  route: string;
  correlation_id?: string;
  traceparent?: string;
  failure_class: HttpFailureClass;
  app_version: string;
  fullstory_url?: string;
  env: string;
}

export interface AmplifyAuthErrorLogContext {
  operation: string;
  error: unknown;
}

export interface AmplifyAuthErrorLogPayload {
  operation: string;
  error_name: string;
  failure_class: 'cognito_system';
  http_status_code?: number;
  request_id?: string;
  route: string;
  app_version: string;
  fullstory_url?: string;
  env: string;
}

const EXPECTED_AUTH_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotFoundError',
  'CodeMismatchException',
  'UsernameExistsException',
  'LimitExceededException',
  'InvalidParameterException',
  'InvalidPasswordException',
  'InvalidRegistrationException',
  'IdpUserNotFoundException',
  'AccountRecoveryException',
  'MultipleUsersFoundException',
  'CidLookupException',
  'UserLambdaValidationException',
  'GoogleRecaptchaException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'UserAlreadyAuthenticatedException',
  'UserUnAuthenticatedException',
  'EmptySignInUsername',
  'EmptySignInPassword',
  'EmptySignUpUsername',
  'EmptySignUpPassword',
]);

const SYSTEM_AUTH_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NetworkError',
  'InternalErrorException',
  'TooManyRequestsException',
  'ExternalServiceException',
  'CodeDeliveryFailureException',
  'UnexpectedLambdaException',
  'InvalidLambdaResponseException',
  'TokenRefreshException',
]);

@Injectable({ providedIn: 'root' })
export class HttpErrorLoggerService {
  private readonly enabled = inject(DD_LOGGING_ENABLED);
  private readonly shouldLogClientErrors = inject(DD_LOG_CLIENT_ERRORS);
  private readonly ddLogger = inject(DD_LOGGER);
  private readonly windowRefService = inject(WindowRefService);
  private readonly allowedHosts = buildAllowedHosts(environment);

  logHttpError(context: HttpErrorLogContext): void {
    if (!this.enabled) {
      return;
    }

    const url = context.request?.url ?? '';
    if (SKIP_LOG_URL_PATTERNS.some((pattern) => url.includes(pattern))) {
      return;
    }

    if (!(context.error instanceof HttpErrorResponse)) {
      return;
    }

    const httpError = context.error;
    const status = httpError.status;

    if (!this.isReportableHttpError(httpError, status)) {
      return;
    }

    const failureClass: HttpFailureClass =
      status === 0 ? 'connectivity' : status >= 400 && status < 500 ? 'client' : 'backend';
    const payload = this.buildHttpPayload(context, httpError, status, failureClass);
    const messageSuffix = failureClass === 'connectivity' ? ' (connectivity failure)' : '';
    const message = `HTTP ${status} ${payload.method} ${payload.url_template}${messageSuffix}`;

    try {
      this.ddLogger.error(message, payload);
    } catch {
      // never break the interceptor chain on a logging failure
    }
  }

  logAmplifyAuthError(context: AmplifyAuthErrorLogContext): void {
    if (!this.enabled) {
      return;
    }

    if (!this.isSystemAmplifyError(context.error)) {
      return;
    }

    const payload = this.buildAmplifyPayload(context);
    const message = `Amplify ${payload.operation} ${payload.error_name}`;

    try {
      this.ddLogger.error(message, payload);
    } catch {
      // never break the auth flow on a logging failure
    }
  }

  private isReportableHttpError(httpError: HttpErrorResponse, status: number): boolean {
    if (status >= 500) {
      return true;
    }

    if (this.shouldLogClientErrors && status >= 400 && status < 500) {
      return true;
    }

    if (status !== 0) {
      return false;
    }

    if (this.isAbortedRequest(httpError)) {
      return false;
    }

    if (this.isBrowserOffline()) {
      return false;
    }

    return true;
  }

  private isAbortedRequest(httpError: HttpErrorResponse): boolean {
    const errorBody = httpError.error;
    if (errorBody instanceof ProgressEvent && errorBody.type === 'abort') {
      return true;
    }
    if (typeof errorBody === 'object' && errorBody !== null) {
      const type = (errorBody as { type?: unknown }).type;
      if (type === 'abort') {
        return true;
      }
    }
    return false;
  }

  private isBrowserOffline(): boolean {
    try {
      return this.windowRefService.nativeWindow?.navigator?.onLine === false;
    } catch {
      return false;
    }
  }

  private isSystemAmplifyError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const authError = error as {
      name?: string;
      metadata?: { httpStatusCode?: number; requestId?: string };
    };
    const errorName = authError.name ?? '';

    if (EXPECTED_AUTH_ERROR_NAMES.has(errorName)) {
      return false;
    }

    if (SYSTEM_AUTH_ERROR_NAMES.has(errorName)) {
      return true;
    }

    const httpStatusCode = authError.metadata?.httpStatusCode;
    if (httpStatusCode === 0 || httpStatusCode === 429 || (httpStatusCode != null && httpStatusCode >= 500)) {
      return true;
    }

    return false;
  }

  private buildHttpPayload(
    context: HttpErrorLogContext,
    httpError: HttpErrorResponse,
    status: number,
    failureClass: HttpFailureClass
  ): HttpErrorLogPayload {
    const url = context.request?.url ?? '';
    const correlationId = this.getCorrelationId(httpError);
    const traceparent = this.getTraceparent(httpError);

    return {
      status,
      method: context.request?.method ?? 'UNKNOWN',
      url_template: maskUrlForLogging(url, this.allowedHosts),
      route: maskRoutePath(this.getRoute()),
      correlation_id: correlationId,
      traceparent: traceparent !== correlationId ? traceparent : undefined,
      failure_class: failureClass,
      app_version: environment.appVersion,
      fullstory_url: this.getFullStorySessionUrl(),
      env: environment.env,
    };
  }

  private buildAmplifyPayload(context: AmplifyAuthErrorLogContext): AmplifyAuthErrorLogPayload {
    const authError = context.error as {
      name?: string;
      metadata?: { httpStatusCode?: number; requestId?: string };
    };

    return {
      operation: context.operation,
      error_name: authError.name ?? 'UnknownError',
      failure_class: 'cognito_system',
      http_status_code: authError.metadata?.httpStatusCode,
      request_id: authError.metadata?.requestId,
      route: maskRoutePath(this.getRoute()),
      app_version: environment.appVersion,
      fullstory_url: this.getFullStorySessionUrl(),
      env: environment.env,
    };
  }

  private getRoute(): string {
    try {
      return this.windowRefService.nativeWindow?.location?.pathname ?? '';
    } catch {
      return '';
    }
  }

  private getCorrelationId(error: HttpErrorResponse): string | undefined {
    try {
      const headers = error.headers;
      if (!headers || typeof headers.get !== 'function') return undefined;
      return headers.get('x-correlation-id') || headers.get('X-Correlation-Id') || undefined;
    } catch {
      return undefined;
    }
  }

  private getTraceparent(error: HttpErrorResponse): string | undefined {
    try {
      const headers = error.headers;
      if (!headers || typeof headers.get !== 'function') return undefined;
      return headers.get('traceparent') || undefined;
    } catch {
      return undefined;
    }
  }

  private getFullStorySessionUrl(): string | undefined {
    try {
      const fullStory = this.windowRefService.nativeWindow?.FS;
      return typeof fullStory?.getCurrentSessionURL === 'function'
        ? fullStory.getCurrentSessionURL()
        : undefined;
    } catch {
      return undefined;
    }
  }
}
